import { ArrowLeft, Bell, Menu } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";

interface MobileJobHeaderProps {
  job: {
    job_number: string;
    owner_name: string;
    property_address: string;
    property_type: string;
    status: string;
    estimated_value?: number;
  };
  onMenuOpen: () => void;
}

export const MobileJobHeader = ({ job, onMenuOpen }: MobileJobHeaderProps) => {
  const navigate = useNavigate();

  return (
    <div className="sticky top-0 z-50 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80 border-b">
      {/* Top bar with branding and actions */}
      <div className="flex items-center justify-between px-4 py-3">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            className="h-9 w-9"
            onClick={() => navigate('/jobs')}
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-lg bg-primary flex items-center justify-center">
              <span className="text-white font-bold text-sm">RG</span>
            </div>
            <div>
              <div className="text-sm font-bold">RoofGenius</div>
              <div className="text-xs text-muted-foreground">AI</div>
            </div>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="icon" className="h-9 w-9">
            <Bell className="h-5 w-5" />
          </Button>
          <Button variant="ghost" size="icon" className="h-9 w-9" onClick={onMenuOpen}>
            <Menu className="h-5 w-5" />
          </Button>
        </div>
      </div>

      {/* Job info section */}
      <div className="px-4 pb-4">
        <div className="flex items-center gap-2 mb-2">
          <Badge variant="outline" className="text-xs px-2 py-0.5">
            {job.job_number}
          </Badge>
          <Badge 
            className={`text-xs px-2 py-0.5 ${
              job.property_type?.toLowerCase() === 'insurance' 
                ? 'bg-blue-500/10 text-blue-600 border-blue-500/30' 
                : 'bg-green-500/10 text-green-600 border-green-500/30'
            }`}
          >
            {job.property_type?.toLowerCase() === 'insurance' ? 'Insurance' : job.property_type}
          </Badge>
          <Badge variant="secondary" className="text-xs px-2 py-0.5">
            {job.status}
          </Badge>
        </div>
        
        <h1 className="text-2xl font-bold mb-1">{job.owner_name}</h1>
        
        <div className="flex items-start gap-2 text-sm text-muted-foreground">
          <span>📍</span>
          <span className="flex-1">{job.property_address}</span>
        </div>

        {job.estimated_value && job.estimated_value > 0 && (
          <div className="mt-2 text-sm">
            <span className="text-muted-foreground">Last contacted: </span>
            <span className="font-medium">Today</span>
            <span className="text-muted-foreground mx-2">•</span>
            <span className="text-muted-foreground">Est: </span>
            <span className="font-medium">${job.estimated_value.toLocaleString()}</span>
          </div>
        )}
      </div>
    </div>
  );
};
